// React
import React, { useState } from "react";

// Core
import { Loader } from "Core/Components/Loader";
import { useRouter } from "Core/Utils";
import { sortByString } from "Core/Utils/Utils";

// Material-UI (Core)
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Card, CardActionArea, CardContent, Drawer, IconButton, Modal, Typography } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { Close as CloseIcon } from "@material-ui/icons";
import RemoveIcon from "@material-ui/icons/RemoveCircle";
import AddCircleIcon from "@material-ui/icons/AddCircle";

// Custom
import { Error } from "Custom/Components/Error";
import { DashboardPageStyles, MainPageStyles, usePageAnimationStyles } from "Custom/StylesAppSpecific/DashboardPagesStyling";
import { SnagsPageStyles } from "Custom/StylesAppSpecific/DashboardSubViews/SnagsPageStyling";
import { ApiStatus } from "Custom/Models/Enums/ApiStatus";
import { SnagsViewModel } from "Custom/ViewModels/DashboardSubViewModels/SnagsViewModel";
import { SnagViewModel } from "Custom/ViewModels/DashboardSubViewModels/SnagViewModel";
import { NewSnag } from "Custom/Views/DashboardSubViews/NewSnag";
import { useDocumentsPageStyles } from "Custom/StylesAppSpecific/DashboardSubViews/DocumentPageStyling";

// Other
import { useObserver } from "mobx-react-lite";
import { ViewSnag } from "./ViewSnag";

// SVG
import { ReactComponent as CircleRightArrow } from "../../Content/substraction.svg";

// Custom ViewModels
import { DashboardViewModel } from "Custom/ViewModels/DashboardViewModel";
import { SnagStatusType } from "Custom/Models/Enums/SnagStatusType";

export const Snags: React.FC = () => {
    // #region Code Behind
    const router = useRouter();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up("md")) ? true : false;

    const propertyId = (router.match.params as any).propertyId;

    const pageAnimationStyles = usePageAnimationStyles();
    const dashboardPageStyles = DashboardPageStyles();
    const documentsPageStyles = useDocumentsPageStyles();
    const mainPageStyles = MainPageStyles();
    const snagsPageStyles = SnagsPageStyles();

    const [viewModel] = useState(() => new SnagsViewModel(propertyId));
    const [isModalOpen, setModalOpen] = useState(false);

    // Use the shared viewModel
    const [dashboardViewModel] = useState(() => new DashboardViewModel(propertyId, false));

    const [expanded, setExpanded] = useState<string | false>(false);

    const handleOpenModal = () => setModalOpen(true);
    const handleCloseModal = () => setModalOpen(false);

    const [drawerOpen, setDrawerOpen] = React.useState(false);
    const [selectedSnagId, setSelectedSnagId] = React.useState<string | null>(null);

    const handleDrawerOpen = (snagId: string) => {
        setSelectedSnagId(snagId);
        setDrawerOpen(true);
    };

    const handleDrawerClose = () => {
        setDrawerOpen(false);
        setSelectedSnagId(null);
    };

    const handleAccordionChange = (panel: string) => (event: React.ChangeEvent<{}>, isExpanded: boolean) => {
        setExpanded(isExpanded ? panel : false);
    };

    const getSnagDateClasses = (snagViewModel: SnagViewModel): string => {
        let classes = snagsPageStyles.snagTitle;

        if (snagViewModel.isCompleted) {
            classes = `${classes} ${snagsPageStyles.resolvedSnagTitle}`;
        } else if (snagViewModel.isRejected) {
            classes = `${classes} ${snagsPageStyles.rejectedSnagTitle}`;
        }

        return classes;
    };
    // #endregion Code Behind

    const renderBusy = () => <Loader loadingText="Fetching snags..." />;

    const renderError = () => <Error errorText="Failed to retrieve the snags." />;

    const renderSnags = (snags: SnagViewModel[], title: string) => {
        return (
            <Box className={snagsPageStyles.snagsRow}>
                {snags &&
                    snags
                        .slice()
                        .sort((a, b) => {
                            let dateA, dateB;

                            if (title === "Completed") {
                                dateA = a.model.resolvedDate;
                                dateB = b.model.resolvedDate;
                            } else if (title === "Rejected") {
                                dateA = a.model.snagStatusUpdatedDate;
                                dateB = b.model.snagStatusUpdatedDate;
                            } else {
                                dateA = a.model.reportedDate;
                                dateB = b.model.reportedDate;
                            }

                            return sortByString(dateB, dateA);
                        })
                        .map((snagViewModel, index) => (
                            <Box key={index} style={{ display: "block", marginTop: "auto" }}>
                                <Box
                                    className={snagsPageStyles.statusBanner}
                                    style={{ display: snagViewModel.snagStatusText ? "block" : "none", background: snagViewModel.snagStatusBackground }}
                                >
                                    {snagViewModel.snagStatusText && (
                                        <Typography className={snagsPageStyles.snagTitle} component="h2">
                                            {snagViewModel.snagStatusText}
                                        </Typography>
                                    )}
                                </Box>
                                <Card className={snagsPageStyles.snag} key={index}>
                                    <CardActionArea>
                                        <CardContent>
                                            <Box className={snagsPageStyles.cardHead}>
                                                <Box>
                                                    <Typography className={snagsPageStyles.snagTitle} component="h4">
                                                        {snagViewModel.NumberAndArea}
                                                    </Typography>
                                                    <Typography className={""} component="p">
                                                        {snagViewModel.Title}
                                                    </Typography>
                                                </Box>
                                                <Box
                                                    className={snagsPageStyles.navigateToArrow}
                                                    onClick={() =>
                                                        isDesktop ? viewModel.naviagateToViewSnag(propertyId, snagViewModel.model.id!) : handleDrawerOpen(snagViewModel.model.id!)
                                                    }
                                                >
                                                    <CircleRightArrow />
                                                </Box>
                                            </Box>

                                            <Box className={snagsPageStyles.snagContentRow}>
                                                <Box>
                                                    <Typography className={snagsPageStyles.snagDetails} variant="body2">
                                                        {snagViewModel.Details.length > 50 ? `${snagViewModel.Details.substring(0, 50)}...` : snagViewModel.Details}
                                                    </Typography>

                                                    <Typography className={getSnagDateClasses(snagViewModel)} variant="body2">
                                                        {snagViewModel.FormattedSnagStatus}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </CardContent>
                                    </CardActionArea>
                                </Card>
                            </Box>
                        ))}
            </Box>
        );
    };

    const renderFormHeader = () => {
        return (
            <>
                <Box className={`baseHeader ${snagsPageStyles.sectionHeader}`}>
                    <Typography variant="h5">
                        Your snags
                        <Typography component="span">{`(${dashboardViewModel.dashboardDetail.propertyAddress})`}</Typography>
                    </Typography>

                    {isDesktop && (
                        <Button className={snagsPageStyles.reportBtn} variant="contained" color="primary" onClick={handleOpenModal}>
                            Report a snag
                        </Button>
                    )}
                </Box>

                {/* Modal for the NewSnag component */}
                {isDesktop ? (
                    <Modal
                        className={snagsPageStyles.reportSnagModal}
                        open={isModalOpen}
                        onClose={handleCloseModal}
                        aria-labelledby="new-snag-modal-title"
                        aria-describedby="new-snag-modal-description"
                    >
                        <Box className={snagsPageStyles.reportSnagModalBox}>
                            <Box className={snagsPageStyles.reportSnagModalHead}>
                                <Typography id="new-snag-modal-title" variant="h5">
                                    Report a Snag
                                </Typography>
                                <IconButton onClick={() => setModalOpen(false)} className={snagsPageStyles.closeIcon}>
                                    <CloseIcon />
                                </IconButton>
                            </Box>
                            <Box className={snagsPageStyles.reportSnagModalBody}>
                                <NewSnag viewModel={viewModel.newSnagViewModel} onClose={() => setModalOpen(false)} />
                            </Box>
                        </Box>
                    </Modal>
                ) : (
                    <>
                        <Drawer anchor="bottom" open={isModalOpen} onClose={handleCloseModal} className="reportSnagModalBoxMobile">
                            <Box className={snagsPageStyles.reportSnagModalBox}>
                                <Box className={snagsPageStyles.reportSnagModalHead}>
                                    <Typography id="new-snag-modal-title" variant="h5">
                                        Report a Snag
                                    </Typography>
                                    <IconButton onClick={handleCloseModal} className={snagsPageStyles.closeIcon}>
                                        <CloseIcon />
                                    </IconButton>
                                </Box>
                                <Box className={snagsPageStyles.reportSnagModalBody}>
                                    <NewSnag viewModel={viewModel.newSnagViewModel} onClose={handleCloseModal} />
                                </Box>
                            </Box>
                        </Drawer>
                    </>
                )}
            </>
        );
    };

    const renderSnagSection = (title: string) => {
        const panel = title.replace(" ", "").toLowerCase();
        let snags = viewModel.SnagViewModels;

        if (title == "Completed") {
            snags = snags.filter(s => s.isCompleted);
        } else if (title == "Rejected") {
            snags = snags.filter(s => s.isRejected);
        } else {
            snags = snags.filter(s => !s.isCompleted && !s.isRejected);
        }

        return (
            <Box style={{ paddingBottom: "0px" }} className={dashboardPageStyles.workspace}>
                <Accordion className={snagsPageStyles.accordionCard} expanded={expanded === panel} onChange={handleAccordionChange(panel)}>
                    <AccordionSummary
                        className={`${documentsPageStyles.accordionHeader} ${!isDesktop && documentsPageStyles.isMobileaccordionHeader} baseHeader`}
                        style={{ display: "flex", alignItems: "center" }}
                        aria-controls="panel-content"
                        id="panel-header"
                    >
                        {expanded === panel ? <RemoveIcon style={{ marginRight: 10 }} /> : <AddCircleIcon style={{ marginRight: 10 }} />}
                        <Typography className={documentsPageStyles.accordionTitle} variant="h5">
                            {`${title} (${snags.length})`}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails className={snagsPageStyles.accordionDetails}>{renderSnags(snags, title)}</AccordionDetails>
                </Accordion>
            </Box>
        );
    };

    const renderItems = () => {
        return (
            <Box className={`${documentsPageStyles.documentPage} ${!isDesktop && documentsPageStyles.ismobileViewAccordionBlock} baseViewAccordionBlock`}>
                {/* Property Documents Section */}
                {renderSnagSection("Reported")}

                {/* Development Documents Section */}
                {renderSnagSection("Completed")}

                {/* Drawing Documents Section */}
                {renderSnagSection("Rejected")}

                {!isDesktop && (
                    <Button className={snagsPageStyles.reportBtn} variant="contained" color="primary" onClick={handleOpenModal}>
                        Report a snag
                    </Button>
                )}
            </Box>
        );
    };

    return useObserver(() => (
        <Box className={`${dashboardPageStyles.root} ${snagsPageStyles.root} ${pageAnimationStyles.pageWrapper} `}>
            <Box className={`${dashboardPageStyles.workspace} ${mainPageStyles.workspace} snagsMobileView`}>
                {viewModel.ApiStatus === ApiStatus.success && (
                    <React.Fragment>
                        {renderFormHeader()}
                        {renderItems()}
                        {/* {renderSnags()} */}
                    </React.Fragment>
                )}
                {viewModel.ApiStatus === ApiStatus.loading && <React.Fragment>{renderBusy()}</React.Fragment>}
                {viewModel.ApiStatus === ApiStatus.error && <React.Fragment>{renderError()}</React.Fragment>}
            </Box>

            {/* Mobile */}
            <Drawer className="yourSnagsMobileDrawer" anchor="bottom" open={drawerOpen} onClose={handleDrawerClose}>
                {selectedSnagId && <ViewSnag propertyId={propertyId} snagId={selectedSnagId} onDrawerClose={handleDrawerClose} />}
            </Drawer>
        </Box>
    ));
};
